import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Target, Zap, FlaskConical, Compass, CheckCircle2, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface RecommendationFraming {
  is_reversible_test?: boolean;
  framing_type?: "final_direction" | "reversible_test" | "exploratory_pilot";
  framing_explanation?: string;
  test_duration_days?: number;
  success_threshold?: string;
  fallback_option?: string;
}

interface ExecutiveSummaryCompactProps {
  data: {
    executive_intro_paragraph?: string;
    key_points?: string[];
    one_sentence_conclusion?: string;
    decision_readiness_score?: number;
    recommended_option_label?: string;
    confidence_level?: number;
    confidence_reasoning?: string[];
    focus_metric?: string;
    primary_lever?: string;
    recommendation_framing?: RecommendationFraming;
  };
  problemStatement?: string;
  recommendedOption?: string;
}

export function ExecutiveSummaryCompact({ data, problemStatement, recommendedOption }: ExecutiveSummaryCompactProps) {
  if (!data) return null;

  const introParagraph = data.executive_intro_paragraph || "";
  const confidenceLevel = data.confidence_level || data.decision_readiness_score;
  const confidenceReasoning = data.confidence_reasoning || [];
  const optionLabel = data.recommended_option_label || recommendedOption;
  const focusMetric = data.focus_metric;
  const primaryLever = data.primary_lever;
  const framing = data.recommendation_framing;

  // Legacy fallback: build paragraph from key_points (max 2 sentences)
  const buildLegacyParagraph = () => {
    const keyPoints = data.key_points || [];
    if (keyPoints.length === 0) return problemStatement || "";
    return keyPoints.slice(0, 2).join(" ");
  };

  const displayParagraph = introParagraph || buildLegacyParagraph();

  const getConfidenceColor = (level: number | undefined) => {
    if (!level) return "text-muted-foreground";
    if (level >= 70) return "text-green-600 dark:text-green-400";
    if (level >= 50) return "text-amber-600 dark:text-amber-400";
    return "text-red-600 dark:text-red-400";
  };

  const getConfidenceBgColor = (level: number | undefined) => {
    if (!level) return "bg-muted/50 border-muted";
    if (level >= 70) return "bg-green-500/10 border-green-500/30";
    if (level >= 50) return "bg-amber-500/10 border-amber-500/30";
    return "bg-red-500/10 border-red-500/30";
  };

  const getFramingBadge = () => {
    if (!framing?.framing_type) return null;
    
    switch (framing.framing_type) {
      case "exploratory_pilot":
        return (
          <Badge variant="outline" className="bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/30">
            <FlaskConical className="h-3 w-3 mr-1" />
            Explorativer Pilot
          </Badge>
        );
      case "reversible_test":
        return (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30">
            <Compass className="h-3 w-3 mr-1" />
            Reversibler Test ({framing.test_duration_days || 30} Tage)
          </Badge>
        );
      case "final_direction":
        return (
          <Badge variant="outline" className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Richtungsentscheidung
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <Card id="executive-summary" className="border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent scroll-mt-24">
      <CardContent className="pt-6">
        {/* Header Row */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold">Executive Summary</h2>
          </div>
          
          {/* Confidence Badge with Reasoning Tooltip */}
          {confidenceLevel !== undefined && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-help">
                    <Badge 
                      variant="outline" 
                      className={`${getConfidenceColor(confidenceLevel)} ${getConfidenceBgColor(confidenceLevel)}`}
                    >
                      Konfidenz {confidenceLevel}%
                    </Badge>
                    {confidenceReasoning.length > 0 && (
                      <Info className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                  </div>
                </TooltipTrigger>
                {confidenceReasoning.length > 0 && (
                  <TooltipContent side="bottom" align="end" className="max-w-xs">
                    <div className="space-y-1">
                      <p className="font-semibold text-xs">Warum diese Konfidenz?</p>
                      <ul className="text-xs space-y-1">
                        {confidenceReasoning.map((reason, index) => (
                          <li key={index} className="flex items-start gap-1.5">
                            <span className="text-muted-foreground">•</span>
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* COMMITMENT FIRST - Entscheidung vor Begründung */}
        {optionLabel && (
          <div className="bg-primary text-primary-foreground rounded-lg px-4 py-4 mb-4">
            <p className="text-lg font-bold mb-2">
              Wir committen uns auf: {optionLabel}
            </p>
            {framing?.test_duration_days && (
              <p className="text-sm opacity-90">
                Zeitraum: {framing.test_duration_days} Tage
              </p>
            )}
            {framing?.success_threshold && (
              <p className="text-sm opacity-90 mt-1">
                Erfolgskriterium: {framing.success_threshold}
              </p>
            )}
            <div className="mt-3 flex items-center gap-2">
              {getFramingBadge()}
            </div>
          </div>
        )}

        {/* KEY METRICS - Bullet Style for C-Level */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {focusMetric && (
            <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
              <Target className="h-4 w-4 text-primary shrink-0" />
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Fokus-Metrik</p>
                <p className="text-sm font-semibold">{focusMetric}</p>
              </div>
            </div>
          )}
          {primaryLever && (
            <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
              <Zap className="h-4 w-4 text-amber-500 shrink-0" />
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Haupthebel</p>
                <p className="text-sm font-semibold">{primaryLever}</p>
              </div>
            </div>
          )}
        </div>

        {/* Begründung NACH Commitment - kurz und prägnant */}
        {displayParagraph && (
          <p className="text-sm leading-relaxed text-muted-foreground">
            {displayParagraph}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
